import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { animate, motion, useMotionTemplate, useMotionValue, useTransform } from "framer-motion";
import { HEBREW_BASE_LETTERS, hebrewLetterToNote, normalizeHebrewLetter } from "@/lib/hebrewCryptogram";

// Which of the 22 strings an entry belongs to, whether it came from the
// sequence builder (baseLetter) or a recorded event (letter).
export function letterIndexOf(entry) {
  if (!entry) return -1;
  const base = entry.baseLetter || normalizeHebrewLetter(entry.letter || "");
  return HEBREW_BASE_LETTERS.indexOf(base);
}

// A 22-string harp read the way Hebrew is read: aleph is the longest, lowest
// string on the right, tav the shortest and highest on the left.
const W = 1160;
const H = 560;
const PAD_L = 66;
const PAD_R = 168;
const STEP = (W - PAD_L - PAD_R) / (HEBREW_BASE_LETTERS.length - 1);
const NECK_LEFT_Y = 164;
const NECK_RIGHT_Y = 52;
const BOARD_LEFT_Y = 336;
const BOARD_RIGHT_Y = 486;
// How long a string reads as "ringing" after it's plucked.
const RING_MS = 700;

// Physical keys, left to right across the strings.
export const HARP_KEYS = "qwertyuiop[]asdfghjkl;".split("");

const xAt = (d) => PAD_L + d * STEP;
// The neck sweeps up towards the long strings — a parabola, not a straight line.
const topAt = (d) => {
  const t = d / (HEBREW_BASE_LETTERS.length - 1);
  return NECK_LEFT_Y + (NECK_RIGHT_Y - NECK_LEFT_Y) * t ** 1.55;
};
const bottomAt = (d) => {
  const t = d / (HEBREW_BASE_LETTERS.length - 1);
  return BOARD_LEFT_Y + (BOARD_RIGHT_Y - BOARD_LEFT_Y) * t;
};

/**
 * The 22 strings in display order: tav on the left, aleph on the right.
 * Each carries its letter, its note and its geometry.
 */
export function harpStrings(scaleOptions = {}) {
  return HEBREW_BASE_LETTERS.map((letter, letterIdx) => ({
    letter,
    letterIdx,
    ...(hebrewLetterToNote(letter, { ...scaleOptions, condensed: false }) || {}),
  }))
    .reverse()
    .map((s, d) => ({
      ...s,
      d,
      x: xAt(d),
      top: topAt(d),
      bottom: bottomAt(d),
      // Low strings are fat, high strings are hairs.
      width: 1 + 2.6 * (d / (HEBREW_BASE_LETTERS.length - 1)) ** 1.4,
    }));
}

const pathFor = (s, bend) =>
  `M ${s.x.toFixed(1)} ${s.top.toFixed(1)} Q ${(s.x + bend).toFixed(1)} ${(
    (s.top + s.bottom) / 2
  ).toFixed(1)} ${s.x.toFixed(1)} ${s.bottom.toFixed(1)}`;

function HarpString({ s, trigger, lit, active, P }) {
  const amp = 7 + s.width * 1.6;
  // The bend is a motion value and the path is built from it, so rapid plucks
  // never hand framer a keyframe array of path strings to interpolate.
  const bend = useMotionValue(0);
  const cx = useTransform(bend, (b) => (s.x + b).toFixed(1));
  const midY = ((s.top + s.bottom) / 2).toFixed(1);
  const d = useMotionTemplate`M ${s.x.toFixed(1)} ${s.top.toFixed(1)} Q ${cx} ${midY} ${s.x.toFixed(1)} ${s.bottom.toFixed(1)}`;

  useEffect(() => {
    if (!trigger) return undefined;
    const controls = animate(bend, [amp, -amp * 0.7, amp * 0.42, -amp * 0.2, 0], {
      duration: 0.62,
      ease: "easeOut",
    });
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  return (
    <g>
      {(lit || active) && (
        <path
          d={pathFor(s, 0)}
          stroke={active ? P.active : P.lit}
          strokeWidth={s.width + 6}
          strokeLinecap="round"
          fill="none"
          opacity={active ? 0.3 : 0.14}
          style={{ filter: "blur(4px)" }}
        />
      )}
      <motion.path
        data-testid={`harp-string-${s.letterIdx}`}
        data-letter={s.letter}
        data-pitch={s.pitch}
        data-ringing={active ? "1" : "0"}
        d={d}
        fill="none"
        strokeLinecap="round"
        stroke={active ? P.active : lit ? P.lit : P.string}
        strokeWidth={s.width}
        opacity={0.85}
      />
    </g>
  );
}

/**
 * Props: scaleOptions, onPluck(string), lit (Set of letterIdx already played),
 * disabled. Plucks triggered from outside (playback, keyboard) come through the
 * ref: `harpRef.current.pluck(letterIdx)`. Each string keeps its own counter,
 * so two strings hit at the same moment both vibrate.
 */
const Harp = forwardRef(function Harp(
  { scaleOptions = {}, onPluck = null, lit = null, disabled = false, theme = "dark", footer = null },
  ref
) {
  // "light" is the public Jewish Music Lab theme; the studio stays dark.
  const P =
    theme === "light"
      ? {
          panelBg: "#FFFBF2",
          panelBorder: "#EBE3D5",
          string: "#A98430",
          lit: "#D99B26",
          active: "#E86F4A",
          wood: ["#C19A5B", "#9A7334", "#6F5021"],
          brass: ["#A97E2A", "#E6BD5E", "#A97E2A"],
          letter: "#1A1523",
          letterActive: "#C04A22",
          pitch: "#8C8198",
          keyCap: "#B6AEC4",
          pillarEdge: "#D99B26",
        }
      : {
          panelBg: "#0C0826",
          panelBorder: "#1A0F3C",
          string: "#D8C48B",
          lit: "#F2D589",
          active: "#FCA07A",
          wood: ["#4A2A63", "#2D1846", "#170D2E"],
          brass: ["#8E7434", "#F2D589", "#8E7434"],
          letter: "#F5EFE3",
          letterActive: "#FCA07A",
          pitch: "#C6BBA9",
          keyCap: "#6F6486",
          pillarEdge: "#F2D589",
        };
  const strings = useMemo(() => harpStrings(scaleOptions), [scaleOptions]);
  // letterIdx -> how many times it's been plucked, and whether it's ringing now.
  const [counts, setCounts] = useState({});
  const [ringing, setRinging] = useState(() => new Set());
  const timersRef = useRef({});
  const strumTimersRef = useRef(new Set());
  const dragRef = useRef(new Map());
  const svgRef = useRef(null);

  useEffect(
    () => () => {
      Object.values(timersRef.current).forEach(clearTimeout);
      strumTimersRef.current.forEach(clearTimeout);
      document.body.style.overscrollBehavior = "";
      document.body.style.touchAction = "";
    },
    []
  );

  // iOS still scrolls on touchmove even with touch-action: none, so the harp
  // swallows the gesture itself.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;
    const swallow = (e) => {
      if (e.cancelable) e.preventDefault();
    };
    svg.addEventListener("touchmove", swallow, { passive: false });
    svg.addEventListener("touchstart", swallow, { passive: false });
    return () => {
      svg.removeEventListener("touchmove", swallow);
      svg.removeEventListener("touchstart", swallow);
    };
  }, []);

  const vibrate = useCallback((letterIdx) => {
    if (letterIdx == null || letterIdx < 0) return;
    setCounts((prev) => ({ ...prev, [letterIdx]: (prev[letterIdx] || 0) + 1 }));
    setRinging((prev) => {
      if (prev.has(letterIdx)) return prev;
      const next = new Set(prev);
      next.add(letterIdx);
      return next;
    });
    clearTimeout(timersRef.current[letterIdx]);
    timersRef.current[letterIdx] = setTimeout(() => {
      setRinging((prev) => {
        if (!prev.has(letterIdx)) return prev;
        const next = new Set(prev);
        next.delete(letterIdx);
        return next;
      });
    }, RING_MS);
  }, []);

  useImperativeHandle(ref, () => ({ pluck: vibrate }), [vibrate]);

  const strike = useCallback(
    (s) => {
      if (disabled || !s) return;
      vibrate(s.letterIdx);
      if (onPluck) onPluck(s);
    },
    [disabled, onPluck, vibrate]
  );

  // One handler for mouse and touch: work out which string the pointer is over
  // and pluck it, so a drag across the harp glissandos. Tracked per pointer id
  // so two fingers can play at once.
  const indexFromEvent = (e) => {
    const svg = svgRef.current;
    if (!svg) return -1;
    const box = svg.getBoundingClientRect();
    const x = ((e.clientX - box.left) / box.width) * W;
    const y = ((e.clientY - box.top) / box.height) * H;
    const d = Math.round((x - PAD_L) / STEP);
    if (d < 0 || d >= strings.length) return -1;
    const s = strings[d];
    // Only count it if the pointer is actually in the string's span.
    if (y < s.top - 30 || y > s.bottom + 30) return -1;
    return d;
  };

  const handleDown = (e) => {
    const d = indexFromEvent(e);
    dragRef.current.set(e.pointerId, { d, t: performance.now() });
    // Stop the page scrolling underneath a strum on a touchscreen.
    if (d >= 0) {
      document.body.style.overscrollBehavior = "none";
      document.body.style.touchAction = "none";
      strike(strings[d]);
    }
  };
  // A fast swipe crosses several strings inside one pointer event. Firing them
  // together sounds like a cluster; a real strum fans them out, so the skipped
  // strings get scheduled a few milliseconds apart.
  const handleMove = (e) => {
    const prev = dragRef.current.get(e.pointerId);
    if (!prev) return;
    const d = indexFromEvent(e);
    if (d < 0 || d === prev.d) return;
    const now = performance.now();
    dragRef.current.set(e.pointerId, { d, t: now });
    if (prev.d < 0) {
      strike(strings[d]);
      return;
    }
    const dir = d > prev.d ? 1 : -1;
    const crossed = [];
    for (let i = prev.d + dir; i !== d + dir; i += dir) crossed.push(i);
    const dt = Math.max(0, now - prev.t);
    const step = Math.min(45, Math.max(11, dt / crossed.length));
    crossed.forEach((idx, i) => {
      if (i === 0) {
        strike(strings[idx]);
        return;
      }
      const t = setTimeout(() => {
        strumTimersRef.current.delete(t);
        strike(strings[idx]);
      }, i * step);
      strumTimersRef.current.add(t);
    });
  };
  const endDrag = (e) => {
    dragRef.current.delete(e.pointerId);
    if (dragRef.current.size === 0) {
      document.body.style.overscrollBehavior = "";
      document.body.style.touchAction = "";
    }
  };

  const neckPath = strings
    .map((s, i) => `${i === 0 ? "M" : "L"} ${s.x.toFixed(1)} ${s.top.toFixed(1)}`)
    .join(" ");
  const boardPath = strings
    .map((s, i) => `${i === 0 ? "M" : "L"} ${s.x.toFixed(1)} ${s.bottom.toFixed(1)}`)
    .join(" ");
  const last = strings[strings.length - 1];

  return (
    <div
      className="rounded-md border overflow-hidden"
      style={{ borderColor: P.panelBorder, backgroundColor: P.panelBg }}
      data-testid="harp"
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto select-none"
        style={{ touchAction: "none", cursor: disabled ? "default" : "pointer" }}
        onPointerDown={handleDown}
        onPointerMove={handleMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onPointerCancel={endDrag}
      >
        <defs>
          <linearGradient id="harpWood" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={P.wood[0]} />
            <stop offset="55%" stopColor={P.wood[1]} />
            <stop offset="100%" stopColor={P.wood[2]} />
          </linearGradient>
          <linearGradient id="harpBrass" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={P.brass[0]} />
            <stop offset="50%" stopColor={P.brass[1]} />
            <stop offset="100%" stopColor={P.brass[2]} />
          </linearGradient>
        </defs>

        {/* Soundboard the strings are pinned to */}
        <path
          d={boardPath}
          stroke="url(#harpWood)"
          strokeWidth="24"
          strokeLinecap="round"
          fill="none"
        />
        <path d={boardPath} stroke="url(#harpBrass)" strokeWidth="2" fill="none" opacity="0.75" />

        {/* Neck across the top of the strings */}
        <path
          d={neckPath}
          stroke="url(#harpWood)"
          strokeWidth="22"
          strokeLinecap="round"
          fill="none"
        />
        <path d={neckPath} stroke="url(#harpBrass)" strokeWidth="2" fill="none" opacity="0.75" />

        {/* Pillar down the long-string side, closing the frame */}
        <path
          d={`M ${last.x + 4} ${last.top - 4} C ${last.x + 116} ${last.top + 40}, ${
            last.x + 104
          } ${last.bottom - 60}, ${last.x + 10} ${last.bottom + 6}`}
          stroke="url(#harpWood)"
          strokeWidth="26"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d={`M ${last.x + 4} ${last.top - 4} C ${last.x + 116} ${last.top + 40}, ${
            last.x + 104
          } ${last.bottom - 60}, ${last.x + 10} ${last.bottom + 6}`}
          stroke={P.pillarEdge}
          strokeOpacity="0.28"
          strokeWidth="1.5"
          fill="none"
        />

        {strings.map((s) => (
          <HarpString
            key={s.letterIdx}
            s={s}
            trigger={counts[s.letterIdx] || 0}
            P={P}
            lit={!!lit && lit.has(s.letterIdx)}
            active={ringing.has(s.letterIdx)}
          />
        ))}

        {/* Letter above each string, note name and key below it */}
        {strings.map((s) => (
          <g key={`lbl-${s.letterIdx}`}>
            <text
              x={s.x}
              y={s.top - 22}
              textAnchor="middle"
              fontSize="24"
              fill={ringing.has(s.letterIdx) ? P.letterActive : P.letter}
              opacity={ringing.has(s.letterIdx) ? 1 : 0.85}
              data-testid={`harp-letter-${s.letterIdx}`}
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {s.letter}
            </text>
            <text
              x={s.x}
              y={s.bottom + 32}
              textAnchor="middle"
              fontSize="11"
              fill={P.pitch}
              opacity="0.8"
              style={{ fontFamily: "'Courier Prime', monospace" }}
            >
              {s.pitch}
            </text>
            <text
              x={s.x}
              y={s.bottom + 47}
              textAnchor="middle"
              fontSize="9"
              fill={P.keyCap}
              style={{ fontFamily: "'Courier Prime', monospace" }}
            >
              {HARP_KEYS[s.d] || ""}
            </text>
          </g>
        ))}
      </svg>
      {footer && <div className="px-4 pb-4 -mt-1">{footer}</div>}
    </div>
  );
});

export default Harp;
